import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Paper,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Alert,
  Snackbar,
  Avatar,
  Skeleton,
} from '@mui/material';
import { AccountCircle } from '@mui/icons-material';
import ClassOutlinedIcon from '@mui/icons-material/ClassOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { useNavigate } from 'react-router-dom';
import { fetchStaffMinimal } from '../slices/staffSlice';
import { useDispatch, useSelector } from 'react-redux';
import { createClass, clearCreateError } from '../slices/classesSlice';
import { motion } from 'framer-motion';

const MotionPaper = motion(Paper);

const CreateClassPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { minimal: teachers, minimalStatus } = useSelector((state) => state.staff);
  const { createError, fieldErrors } = useSelector((state) => state.classes);

  const [classData, setClassData] = useState({
    name: '',
    teacher: '',
  });

  const [showSnackbar, setShowSnackbar] = useState(false);

  useEffect(() => {
    // Fetch only teachers (minimal data)
    dispatch(fetchStaffMinimal({ is_archived: false }));
    dispatch(clearCreateError());
  }, [dispatch]);

  useEffect(() => {
    if (createError) {
      setShowSnackbar(true);
    }
  }, [createError]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setClassData((prev) => ({ ...prev, [name]: value }));
    
    if (fieldErrors[name]) {
      dispatch(clearCreateError());
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await dispatch(createClass(classData)).unwrap();
      navigate('/classes');
    } catch (error) {
      console.error('فشل في إنشاء الصف:', error);
    }
  };

  const handleCloseSnackbar = () => {
    setShowSnackbar(false);
    dispatch(clearCreateError());
  };

  // Filter teachers based on search term (keeping the logic but removing search UI)
  const filteredTeachers = teachers.filter(teacher => 
    teacher.is_teacher && 
    !teacher.is_archived
  );

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 720, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ClassOutlinedIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                إضافة صف جديد
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                إنشاء صف دراسي جديد وتعيين المعلم المسؤول عنه
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/classes')}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.6)',
              px: 3,
              py: 1.1,
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
            }}
          >
            العودة للصفوف
          </Button>
        </MotionPaper>

        {/* Form Card */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
          }}
        >
          {/* General error alert */}
          {createError?.message && !fieldErrors.name && !fieldErrors.teacher && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 2,
                fontFamily: 'Cairo, Tajawal, sans-serif',
              }}
            >
              {createError.message}
            </Alert>
          )}

          <div onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Class Name Field */}
              <Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: '#1E1B4B',
                    mb: 1,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                  }}
                >
                  اسم الصف
                </Typography>
                <TextField
                  name="name"
                  variant="outlined"
                  fullWidth
                  required
                  placeholder="أدخل اسم الصف"
                  value={classData.name}
                  onChange={handleChange}
                  error={!!fieldErrors.name}
                  helperText={fieldErrors.name || ''}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                      bgcolor: '#F8F9FF',
                    },
                    '& .MuiFormHelperText-root': {
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                    },
                  }}
                />
              </Box>

              {/* Teacher Selection */}
              <Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: '#1E1B4B',
                    mb: 1,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                  }}
                >
                  اسم المعلم المسؤول
                </Typography>
                <FormControl
                  fullWidth
                  required
                  error={!!fieldErrors.teacher}
                >
                  <InputLabel sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                    اسم المعلم المسؤول
                  </InputLabel>
                  <Select
                    label="اسم المعلم المسؤول"
                    name="teacher"
                    value={classData.teacher}
                    onChange={handleChange}
                    sx={{
                      borderRadius: 2,
                      bgcolor: '#F8F9FF',
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                    }}
                  >
                    {minimalStatus === 'loading' ? (
                      <MenuItem disabled>
                        <Stack useFlexGap direction="row" spacing={2} alignItems="center" width="100%">
                          <Skeleton variant="circular" width={40} height={40} />
                          <Skeleton variant="text" width="60%" />
                        </Stack>
                      </MenuItem>
                    ) : (
                      filteredTeachers.map((teacher) => (
                        <MenuItem key={teacher.id} value={teacher.id}>
                          <Stack useFlexGap direction="row" spacing={2} alignItems="center">
                            <Avatar
                              sx={{
                                width: 36,
                                height: 36,
                                bgcolor: '#4338CA',
                                fontSize: '14px'
                              }}
                            >
                              <AccountCircle />
                            </Avatar>
                            <Box>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}
                              >
                                {teacher.full_name}
                              </Typography>
                              {teacher.employee_id && (
                                <Typography
                                  variant="caption"
                                  sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}
                                >
                                  رقم الموظف: {teacher.employee_id}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </MenuItem>
                      ))
                    )}

                    {filteredTeachers.length === 0 && minimalStatus !== 'loading' && (
                      <MenuItem disabled>
                        <Typography
                          textAlign="center"
                          width="100%"
                          sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}
                        >
                          لا توجد معلمين متاحين
                        </Typography>
                      </MenuItem>
                    )}
                  </Select>

                  {fieldErrors.teacher && (
                    <Typography
                      variant="caption"
                      sx={{ mt: 0.5, mr: 2, color: '#DC2626', fontFamily: 'Cairo, Tajawal, sans-serif' }}
                    >
                      {fieldErrors.teacher}
                    </Typography>
                  )}
                </FormControl>
              </Box>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                onClick={handleSubmit}
                startIcon={<SaveOutlinedIcon />}
                sx={{
                  bgcolor: '#4338CA',
                  color: '#fff',
                  py: 1.4,
                  borderRadius: 2.5,
                  boxShadow: 'none',
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
                }}
              >
                حفظ الصف
              </Button>
            </Stack>
          </div>
        </MotionPaper>
      </Box>

      {/* Snackbar for errors */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: '100%', borderRadius: 2, fontFamily: 'Cairo, Tajawal, sans-serif' }}
        >
          {createError?.message || 'حدث خطأ في إنشاء الصف'}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateClassPage;